import type { UpcomingItem } from '../types.js';

const MONTH_FORMAT = new Intl.DateTimeFormat('fr-FR', { month: 'short' });

interface MonthReleasesListProps {
  readonly entries: ReadonlyArray<UpcomingItem>;
  readonly onEntryClick?: (entry: UpcomingItem) => void;
}

/**
 * Liste des sorties à venir pour un mois donné, style maquette
 * (pill date à gauche, titre/auteur/série, bouton "+ Rappel").
 */
export function MonthReleasesList({
  entries,
  onEntryClick,
}: MonthReleasesListProps): JSX.Element {
  if (entries.length === 0) {
    return (
      <p className="rounded-2xl bg-paper p-4 text-sm text-brown-soft">
        Aucune sortie programmée ce mois-ci.
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      {entries.map((entry) => (
        <ReleaseItem
          key={entry.id}
          entry={entry}
          onClick={onEntryClick ? () => onEntryClick(entry) : undefined}
        />
      ))}
    </div>
  );
}

function ReleaseItem({
  entry,
  onClick,
}: {
  entry: UpcomingItem;
  onClick?: () => void;
}): JSX.Element {
  const date = entry.book.releaseDate ? new Date(entry.book.releaseDate) : null;
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className="release-item"
      aria-label={`Ouvrir ${entry.book.title}`}
    >
      <div className="release-date">
        <span className="day">{date ? date.getDate() : '—'}</span>
        <span className="month">
          {date ? MONTH_FORMAT.format(date) : ''}
        </span>
      </div>
      <div className="release-info">
        <div className="release-title">{entry.book.title}</div>
        <div className="release-author">{entry.book.author}</div>
        <div className="release-meta">📚 {entry.book.genre}</div>
      </div>
      <span aria-hidden className="release-preorder">
        + Rappel
      </span>
    </div>
  );
}
